import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useJobillaOrgId } from '@/hooks'
import { useWhatsAppTemplates } from '@/hooks/use-whatsapp-templates'
import { Button, emitUnhandledError, logger } from '@/ui'
import {
	mapApiTemplateToWhatsAppTemplate,
	type WhatsAppTemplate,
} from '@/ui/views/campaign-card/candidate-card/whatsapp-thread'
import {
	useBulkCandidateWhatsAppStore,
	whatsAppAccountInactivityQueryKey,
	whatsAppEmbeddedSignupStatusQueryKey,
} from '@jobilla/nexus-sdk'
import type { BulkCandidateWhatsAppResultData, WhatsAppInactivityData } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { CircleCheckIcon, MessageCircleIcon, SendIcon, XIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const LARGE_SELECTION_THRESHOLD = 50
const SIGNUP_STATUS_REFRESH_DELAY_MS = 10_000

export interface BulkWhatsAppModalProps {
	isOpen: boolean
	onClose: () => void
	candidateIds: Set<string>
	campaignId: string
	onSuccess: () => void
}

export function BulkWhatsAppModal({
	isOpen,
	onClose,
	candidateIds,
	campaignId,
	onSuccess,
}: BulkWhatsAppModalProps) {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()

	const [selectedTemplateId, setSelectedTemplateId] = useState('')
	const [parameters, setParameters] = useState<string[]>([])
	const [result, setResult] = useState<BulkCandidateWhatsAppResultData | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	const { templates: apiTemplates } = useWhatsAppTemplates(organisationId, { enabled: isOpen })

	const templates: WhatsAppTemplate[] = useMemo(
		() => apiTemplates.map(mapApiTemplateToWhatsAppTemplate),
		[apiTemplates],
	)

	const signupStatusRefreshRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => () => {
		if (signupStatusRefreshRef.current) { clearTimeout(signupStatusRefreshRef.current) }
	}, [])

	const { mutateAsync: sendBulkWhatsApp, isPending: isSending } = useBulkCandidateWhatsAppStore({
		mutationKey: ['bulk-candidate-whatsapp'],
		onSuccess: (bulkResult) => {
			if (bulkResult.queued <= 0) { return }

			// Sends are queued, so the server still reports the warning until a message is created.
			queryClient.setQueryData<WhatsAppInactivityData>(
				whatsAppAccountInactivityQueryKey(organisationId),
				{ scheduledDeactivationAt: null },
			)
			if (signupStatusRefreshRef.current) { clearTimeout(signupStatusRefreshRef.current) }
			signupStatusRefreshRef.current = setTimeout(() => {
				void queryClient.invalidateQueries({ queryKey: whatsAppEmbeddedSignupStatusQueryKey(organisationId) })
			}, SIGNUP_STATUS_REFRESH_DELAY_MS)
		},
	})

	const candidateCount = candidateIds.size
	const recipientLabel = t('bulkWhatsApp.candidateCount', { count: candidateCount })

	const selectedTemplate = useMemo(
		() => templates.find((template) => template.id === selectedTemplateId),
		[templates, selectedTemplateId],
	)

	const hasApprovedTemplates = useMemo(
		() => templates.some((template) => template.status === 'APPROVED'),
		[templates],
	)

	const mappedFieldForSlot = useCallback((index: number): string | null => {
		const field = selectedTemplate?.parameterMap?.[String(index + 1)]
		return typeof field === 'string' && field.trim() !== '' ? field : null
	}, [selectedTemplate])

	const mappedFieldLabel = useCallback(
		(field: string): string =>
			t(`whatsappSettings.autofillField.${field.replace('.', '_')}`, { defaultValue: field }),
		[t],
	)

	const previewText = useMemo(() => {
		if (!selectedTemplate) {
			return ''
		}

		let text = selectedTemplate.bodyText
		parameters.forEach((param, index) => {
			const mappedField = mappedFieldForSlot(index)
			const replacement = mappedField
				? `[${mappedFieldLabel(mappedField)}]`
				: param || `[${t('bulkWhatsApp.parameterLabel', { index: index + 1 })}]`
			text = text.replaceAll(`{{${index + 1}}}`, replacement)
		})
		return text
	}, [selectedTemplate, parameters, mappedFieldForSlot, mappedFieldLabel, t])

	const canSend = candidateCount > 0
		&& Boolean(selectedTemplateId)
		&& (!selectedTemplate?.parameterCount
			|| parameters.every((param, index) => mappedFieldForSlot(index) !== null || param.trim()))

	const resetAndClose = useCallback(() => {
		setSelectedTemplateId('')
		setParameters([])
		setResult(null)
		onClose()
	}, [onClose])

	const handleDone = useCallback(() => {
		resetAndClose()
		onSuccess()
	}, [resetAndClose, onSuccess])

	const handleTemplateChange = useCallback((templateId: string) => {
		setSelectedTemplateId(templateId)
		const template = templates.find((option) => option.id === templateId)
		setParameters(Array.from({ length: template?.parameterCount ?? 0 }, () => ''))
	}, [templates])

	const handleParameterChange = useCallback((index: number, value: string) => {
		setParameters((prev) => {
			const next = [...prev]
			next[index] = value
			return next
		})
	}, [])

	const handleSend = useCallback(async () => {
		if (!canSend || isSending) { return }

		try {
			const response = await sendBulkWhatsApp({
				data: {
					campaignId: campaignId,
					candidateIds: Array.from(candidateIds),
					templateId: selectedTemplateId,
					parameters,
				},
			})
			setResult(response)
		} catch (err) {
			logger.error(err, {
				tags: {
					component: 'BulkWhatsAppModal',
					action: 'send',
					campaign_id: campaignId,
					template_id: selectedTemplateId,
					candidate_count: String(candidateCount),
				},
			})
			emitUnhandledError({
				title: t('bulkWhatsApp.errorTitle'),
				description: err instanceof Error ? err.message : t('bulkWhatsApp.errorDefault'),
				cause: err,
			})
		}
	}, [canSend, isSending, sendBulkWhatsApp, campaignId, candidateIds, selectedTemplateId, parameters, t])

	useEffect(() => {
		if (!isOpen || result) { return }

		const handleKeyDown = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canSend && !isSending) {
				event.preventDefault()
				void handleSend()
			}
		}

		const container = containerRef.current
		if (container) {
			container.addEventListener('keydown', handleKeyDown)
			return () => container.removeEventListener('keydown', handleKeyDown)
		}
	}, [isOpen, result, canSend, isSending, handleSend])

	if (!isOpen) { return null }

	const skippedReasons = result
		? Object.entries(
			result.skipped.reduce<Record<string, number>>((counts, entry) => {
				counts[entry.reason] = (counts[entry.reason] ?? 0) + 1
				return counts
			}, {}),
		)
			.map(([reason, amount]) => t(`bulkWhatsApp.skipReason.${reason}`, { amount, defaultValue: reason }))
			.join(', ')
		: ''

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			<div className="absolute inset-0 bg-black/50" onClick={result ? handleDone : resetAndClose} />

			{result
				? (
					<div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-8 flex flex-col items-center text-center">
						<div className="size-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
							<CircleCheckIcon className="size-6 text-green-600" />
						</div>
						<h2 className="text-lg font-semibold text-gray-900 mb-1">
							{t('bulkWhatsApp.successTitle')}
						</h2>
						<p className="text-sm text-gray-500 mb-2">
							{t('bulkWhatsApp.successDescription', { count: result.queued })}
						</p>
						{result.skipped.length > 0 && (
							<p className="text-sm text-amber-600 mb-6">
								{t('bulkWhatsApp.skippedSummary', {
									count: result.skipped.length,
									reasons: skippedReasons,
								})}
							</p>
						)}
						{result.skipped.length === 0 && <div className="mb-6" />}
						<Button type="committal" size="short" onClick={handleDone}>
							{t('bulkWhatsApp.gotIt')}
						</Button>
					</div>
				)
				: (
					<div
						ref={containerRef}
						className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 flex flex-col max-h-[80vh]"
					>
						<div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
							<div className="flex items-center gap-2">
								<MessageCircleIcon className="size-4 text-brand-600" />
								<h2 className="text-sm font-medium text-gray-900">
									{t('bulkWhatsApp.sendingTo', { recipients: recipientLabel })}
								</h2>
							</div>
							<button
								type="button"
								onClick={resetAndClose}
								aria-label={t('common.dismiss')}
								className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
							>
								<XIcon className="size-4" />
							</button>
						</div>

						<div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
							{candidateCount > LARGE_SELECTION_THRESHOLD && (
								<p className="text-xs text-amber-600">
									{t('bulkWhatsApp.largeSelectionWarning', { count: candidateCount })}
								</p>
							)}

							<div>
								<label
									htmlFor="bulk-whatsapp-template"
									className="mb-1 block text-sm font-medium text-gray-700"
								>
									{t('bulkWhatsApp.selectTemplate')}
								</label>
								<Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
									<SelectTrigger id="bulk-whatsapp-template" className="w-full">
										<SelectValue placeholder={t('bulkWhatsApp.chooseTemplate')} />
									</SelectTrigger>
									<SelectContent>
										{templates.map((template) => {
											const isApproved = template.status === 'APPROVED'
											return (
												<SelectItem
													key={template.id}
													value={template.id}
													disabled={!isApproved}
												>
													{template.name} ({template.language})
													{!isApproved && ` - ${template.status.toLowerCase()}`}
												</SelectItem>
											)
										})}
									</SelectContent>
								</Select>
							</div>

							{selectedTemplate && selectedTemplate.parameterCount > 0 && (
								<div className="space-y-3">
									<label className="block text-sm font-medium text-gray-700">
										{t('bulkWhatsApp.templateParameters')}
									</label>
									{Array.from(
										{ length: selectedTemplate.parameterCount },
										(_, index) => {
											const mappedField = mappedFieldForSlot(index)
											return (
												<div key={index}>
													<label
														htmlFor={`bulk-param-${index}`}
														className="mb-1 block text-xs text-gray-500"
													>
														{t('bulkWhatsApp.parameterLabel', { index: index + 1 })}
														{!mappedField && selectedTemplate.parameterExamples?.[index]
															&& (
																<span className="ml-1 text-gray-400">
																	{t('bulkWhatsApp.parameterExample', {
																		example:
																			selectedTemplate.parameterExamples[index],
																	})}
																</span>
															)}
													</label>
													{mappedField
														? (
															<div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
																<span className="text-gray-700">
																	{mappedFieldLabel(mappedField)}
																</span>
																<span className="text-xs text-gray-400">
																	{t('bulkWhatsApp.parameterAutoFilled')}
																</span>
															</div>
														)
														: (
															<input
																id={`bulk-param-${index}`}
																type="text"
																value={parameters[index] ?? ''}
																onChange={(event) =>
																	handleParameterChange(index, event.target.value)}
																placeholder={t('bulkWhatsApp.parameterPlaceholder', {
																	placeholder: `{{${index + 1}}}`,
																})}
																className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all"
															/>
														)}
												</div>
											)
										},
									)}
								</div>
							)}

							{selectedTemplate && (
								<div>
									<label className="mb-1 block text-sm font-medium text-gray-700">
										{t('bulkWhatsApp.messagePreview')}
									</label>
									<div className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
										{previewText || t('bulkWhatsApp.previewEmpty')}
									</div>
								</div>
							)}

							{templates.length === 0 && (
								<p className="py-2 text-center text-sm text-gray-500">
									{t('bulkWhatsApp.noTemplates')}
								</p>
							)}
							{templates.length > 0 && !hasApprovedTemplates && (
								<p className="py-2 text-center text-sm text-amber-600">
									{t('bulkWhatsApp.allPending')}
								</p>
							)}
						</div>

						<div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/30 flex-shrink-0">
							<div className="text-xs text-gray-400">
								Press{' '}
								<kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-[10px]">
									Cmd/Ctrl
								</kbd>
								+
								<kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-[10px]">
									Enter
								</kbd>{' '}
								to send
							</div>
							<div className="flex items-center gap-3">
								<Button type="subtle" size="short" onClick={resetAndClose}>
									{t('common.cancel')}
								</Button>
								<Button
									type="committal"
									size="short"
									onClick={handleSend}
									disabled={!canSend}
									isLoading={isSending}
								>
									<span className="flex items-center gap-2">
										<SendIcon className="size-4" />
										{t('bulkWhatsApp.sendTo', { recipients: recipientLabel })}
									</span>
								</Button>
							</div>
						</div>
					</div>
				)}
		</div>
	)
}
